import React from 'react'
import { LaunchButton } from './LaunchButton'
import {
  galaxyBackgroundImage,
  LaunchPoint,
  LaunchSection as LaunchSectionType,
} from '../containers/launchPageContent'
import styles from '../containers/launchPage.module.scss'

type LaunchSectionProps = {
  section: LaunchSectionType
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  onScrollPoint: (targetId: string) => void
  onInfoPoint: (
    modalKey: Extract<LaunchPoint, { kind: 'info' }>['modalKey'],
  ) => void
}

const renderTitle = (section: LaunchSectionType) => {
  if (!section.accent) return section.title

  const accentIndex = section.title.indexOf(section.accent)

  if (accentIndex === -1) return section.title

  const beforeAccent = section.title.slice(0, accentIndex)
  const afterAccent = section.title.slice(accentIndex + section.accent.length)

  return (
    <>
      {beforeAccent}
      <span className={styles.accent}>{section.accent}</span>
      {afterAccent}
    </>
  )
}

const renderMedia = (section: LaunchSectionType) => {
  if (section.media.kind === 'card_collage') {
    return (
      <div
        className={styles.cardCollageFrame}
        aria-label="Examples of digital and printed card designs"
      >
        {section.media.cards.map((card, index) => (
          <img
            key={card.src}
            className={`${styles.collageCard} ${
              card.isHorizontal
                ? styles.collageHorizontalCard
                : styles.collageVerticalCard
            } ${styles[`collageCard${index + 1}` as keyof typeof styles]}`}
            src={card.src}
            alt={card.alt}
          />
        ))}
      </div>
    )
  }

  const mediaClassName = `${styles.mediaFrame} ${
    section.media.kind === 'stack' ? styles.stackMedia : styles.singleMedia
  } ${
    section.media.kind === 'image' &&
    section.media.aspectRatio === 'portrait_2_3'
      ? styles.portraitMedia
      : ''
  } ${section.sectionKey === 'act-on-promptings' ? styles.naturalMedia : ''} ${
    section.sectionKey === 'digital-and-printed'
      ? styles.digitalPrintedMedia
      : ''
  } ${section.sectionKey === 'automation' ? styles.automationMedia : ''}`

  if (section.media.kind === 'stack') {
    return (
      <div className={mediaClassName}>
        <img
          className={`${styles.mediaImage} ${styles.stackPrimary}`}
          src={section.media.src}
          alt={section.media.alt}
        />
        <img
          className={`${styles.mediaImage} ${styles.stackAccent}`}
          src={section.media.accentSrc}
          alt={section.media.accentAlt}
        />
        {section.media.tertiarySrc ? (
          <img
            className={`${styles.mediaImage} ${styles.stackTertiary}`}
            src={section.media.tertiarySrc}
            alt={section.media.tertiaryAlt ?? ''}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div className={mediaClassName}>
      <img
        className={styles.mediaImage}
        src={section.media.src}
        alt={section.media.alt}
        style={{
          objectPosition: section.media.objectPosition ?? 'center center',
        }}
      />
    </div>
  )
}

export const LaunchSection: React.FC<LaunchSectionProps> = ({
  section,
  onPrimaryAction,
  onSecondaryAction,
  onScrollPoint,
  onInfoPoint,
}) => {
  const isDark = section.theme === 'dark_galaxy'
  const isHero = section.sectionKey === 'hero'
  const primaryButtonVariant = isDark ? 'accent' : 'primary'
  const sectionStyle = isHero
    ? ({
        '--launch-hero-bg': galaxyBackgroundImage,
      } as React.CSSProperties)
    : isDark
    ? {
        backgroundImage:
          'linear-gradient(180deg, rgba(5, 7, 14, 0.94), rgba(8, 12, 25, 0.98))',
      }
    : undefined

  return (
    <section
      id={section.sectionKey}
      className={`${styles.section} ${
        isDark
          ? styles.darkSection
          : section.theme === 'soft_gray'
          ? styles.softSection
          : styles.lightSection
      } ${isHero ? styles.heroSection : ''}`}
      style={sectionStyle}
    >
      <div className={styles.shell}>
        <div
          className={`${styles.sectionGrid} ${
            section.layout === 'image_left'
              ? styles.imageLeft
              : styles.imageRight
          }`}
        >
          <div className={styles.mediaColumn}>{renderMedia(section)}</div>

          <div className={styles.contentColumn}>
            <p className={styles.eyebrow}>{section.eyebrow}</p>
            <h2 className={`${styles.title} ${isHero ? styles.heroTitle : ''}`}>
              {renderTitle(section)}
            </h2>
            <p className={styles.body}>{section.body}</p>

            {section.supportingPoints && section.supportingPoints.length > 0 ? (
              <ul className={styles.points}>
                {section.supportingPoints.map(point => {
                  const isActionable =
                    point.kind === 'scroll' || point.kind === 'info'
                  const pointClassName = `${styles.point} ${
                    point.kind === 'scroll'
                      ? styles.scrollPoint
                      : point.kind === 'info'
                      ? styles.infoPoint
                      : styles.staticPoint
                  }`

                  return (
                    <li key={point.label} className={pointClassName}>
                      {isActionable ? (
                        <button
                          className={styles.pointButton}
                          type="button"
                          onClick={() => {
                            if (point.kind === 'scroll') {
                              onScrollPoint(point.targetId)
                              return
                            }

                            if (point.kind === 'info') {
                              onInfoPoint(point.modalKey)
                            }
                          }}
                        >
                          <span className={styles.pointDot} />
                          <span>{point.label}</span>
                          <span
                            className={styles.pointActionIcon}
                            aria-hidden="true"
                          >
                            {point.kind === 'scroll' ? '↓' : '+'}
                          </span>
                        </button>
                      ) : (
                        <>
                          <span className={styles.pointDot} />
                          <span>{point.label}</span>
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : null}

            {(onPrimaryAction || onSecondaryAction) && (
              <div className={styles.actions}>
                {onPrimaryAction ? (
                  <LaunchButton
                    label={section.primaryAction?.label ?? 'View Pricing'}
                    onClick={onPrimaryAction}
                    variant={primaryButtonVariant}
                    pulse={isHero}
                  />
                ) : null}

                {onSecondaryAction ? (
                  <LaunchButton
                    label={section.secondaryAction?.label ?? 'View Pricing'}
                    onClick={onSecondaryAction}
                    variant="secondary"
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
